import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import useBooking from "@/hooks/stores/useBooking";
import useAuth from "@/hooks/stores/useAuth";
import { authService } from "@/services";

export default function Paywall() {
  const navigate = useNavigate();
  const { bookingData } = useBooking();
  const { userFlow, setPaymentStatus } = useAuth();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    console.log(`Selected payment method: ${method}`);
  };

  const handleCompleteBooking = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    console.log('Payment processing started');
    console.log(`Payment method: ${selectedPaymentMethod}`);
    console.log('Amount: £0.00');
    
    try {
      // For remote users, call the payment completion API
      if (userFlow === 'remote') {
        console.log('Calling payment completion API for remote user...');
        const response = await authService.completePayment();
        console.log('Payment completion response:', response);
        
        // Update payment status in auth store
        setPaymentStatus('completed');
        
        toast.success('Payment completed successfully!');
      }
      
      // Navigate to booking success page
      navigate('/booking-success');
    } catch (error) {
      console.error('Payment completion failed:', error);
      
      // Check if the error is due to timeout (1 hour passed)
      if (error.response?.status === 400 || error.response?.data?.message?.includes('expired')) {
        toast.error('Your previous booking attempt has expired. Please book another slot.');
        navigate('/welcome-video');
      } else {
        toast.error('Payment processing failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-secondary rounded-xl border border-secondary-border p-8">
          <h2 className="text-base font-semibold text-foreground mb-1">Billing information</h2>
          <p className="text-base font-normal text-muted-foreground mb-6">Required*</p>

          <div className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  First name<span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 bg-background/50 border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980] cursor-not-allowed"
                  placeholder="John"
                  readOnly
                  disabled
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-foreground mb-2">
                  Last name<span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 bg-background/50 border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980] cursor-not-allowed"
                  placeholder="Doe"
                  readOnly
                  disabled
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-base font-semibold text-foreground mb-2">
                Email address<span className="text-primary">*</span>
              </label>
              <input
                type="email"
                className="w-full px-4 py-2 bg-background/50 border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980] cursor-not-allowed"
                placeholder="john@doe.com"
                readOnly
                disabled
              />
            </div>

            {/* Payment Methods */}
            <div>
              <label className="block text-base font-semibold text-foreground mb-4">Pay with</label>
              <div className="grid grid-cols-4 gap-4">
                <button className="p-6 bg-background border border-secondary-border rounded-[4px] flex items-center justify-center hover:border-primary transition-colors">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-6" />
                </button>
                <button className="p-6 bg-background border border-secondary-border rounded-[4px] flex items-center justify-center hover:border-primary transition-colors">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                </button>
                <button className="p-6 bg-background border border-secondary-border rounded-[4px] flex items-center justify-center hover:border-primary transition-colors">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-6" />
                </button>
                <button className="p-6 bg-background border border-secondary-border rounded-[4px] flex items-center justify-center hover:border-primary transition-colors">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b0/Apple_Pay_logo.svg" alt="Apple Pay" className="h-6" />
                </button>
              </div>
            </div>

            {/* Total Payment */}
            <div className="pt-6">
              <div className="flex justify-between items-center">
                <span className="text-base font-semibold text-foreground">Total payment</span>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-red-600">£0.00</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                No payment required. Early access users can enjoy our services without payment.
              </p>
            </div>

            {/* Complete Payment Button */}
            <button
              onClick={handleCompleteBooking}
              disabled={isProcessing}
              className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors text-[18px] font-semibold flex items-center justify-center gap-2"
            >
              <span>{isProcessing ? 'Processing...' : 'Complete Payment'}</span>
              {!isProcessing && <span>→</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 